import { useEffect, useRef, useState } from 'react';

interface RecordingControlsProps {
  isRecording: boolean;
  isPaused: boolean;
  duration: string;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
}

const RecordingControls = ({
  isRecording,
  isPaused,
  duration,
  onStart,
  onPause,
  onResume,
  onStop,
}: RecordingControlsProps) => {
  const [bars, setBars] = useState<number[]>(Array(20).fill(4));
  const animRef = useRef<number | null>(null);

  useEffect(() => {
    if (isRecording && !isPaused) {
      const animate = () => {
        setBars(Array.from({ length: 20 }, () => Math.floor(Math.random() * 36) + 4));
        animRef.current = requestAnimationFrame(animate);
      };
      animRef.current = requestAnimationFrame(animate);
    } else {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      setBars(Array(20).fill(4));
    }
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [isRecording, isPaused]);

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-slate-800">Recording</h3>
        {isRecording && (
          <span className="flex items-center gap-1.5 text-xs font-medium text-red-500">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block"></span>
            {isPaused ? 'Paused' : 'Live'}
          </span>
        )}
      </div>

      {/* Timer */}
      <div className="text-center mb-5">
        <span className="text-4xl font-mono font-bold text-slate-900 tracking-widest">
          {duration}
        </span>
      </div>

      {/* Waveform */}
      <div className="flex items-center justify-center gap-0.5 h-12 mb-6">
        {bars.map((h, i) => (
          <div
            key={i}
            className="w-1.5 rounded-full transition-all duration-75"
            style={{
              height: `${h}px`,
              background: isRecording && !isPaused
                ? `hsl(${170 + i * 2}, 70%, 50%)`
                : '#cbd5e1',
            }}
          />
        ))}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-3">
        {!isRecording ? (
          <button
            onClick={onStart}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-blue-500 text-white rounded-full font-semibold text-sm shadow-md hover:shadow-lg hover:scale-105 transition-all"
          >
            <i className="ri-mic-fill text-base"></i>
            Start Recording
          </button>
        ) : (
          <>
            {isPaused ? (
              <button
                onClick={onResume}
                className="flex items-center gap-2 px-5 py-2.5 bg-teal-500 text-white rounded-full font-medium text-sm hover:bg-teal-600 transition-colors"
              >
                <i className="ri-play-fill"></i>
                Resume
              </button>
            ) : (
              <button
                onClick={onPause}
                className="flex items-center gap-2 px-5 py-2.5 bg-amber-400 text-white rounded-full font-medium text-sm hover:bg-amber-500 transition-colors"
              >
                <i className="ri-pause-fill"></i>
                Pause
              </button>
            )}
            <button
              onClick={onStop}
              className="flex items-center gap-2 px-5 py-2.5 bg-red-500 text-white rounded-full font-medium text-sm hover:bg-red-600 transition-colors"
            >
              <i className="ri-stop-fill"></i>
              Stop
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default RecordingControls;
